/* eslint-disable max-params */
import React, { useMemo, useState } from 'react';

import './ReservationPanel.scss';
import 'react-big-calendar/lib/css/react-big-calendar.css';
import 'react-big-calendar/lib/addons/dragAndDrop/styles.css';

import { useOrganizationContext } from '../../context/OrganizationContext';
import { useForm } from '../../hooks/useForm';
import { classNames } from 'primereact/utils';
import { useClient } from '../../hooks/useClient';
import useSWR from 'swr';
import { Panel } from '../Panel/Panel';
import { getReservationFormValidators, ReservationForm } from './validators';
import { useAuthContext } from '../../context/AuthContext';
import { User } from '../../types/user';
import { Button, ButtonProps } from 'primereact/button';
import { PrimeIcons } from 'primereact/api';
import { ExternalUserShort } from '../../types/externalUser';
import { Capacitor } from '@capacitor/core';
import { AllowedFeatures } from '../../types/features';
import { useLocaleContext } from '../../context/LocaleContext';
import { RoutinePanel } from '../RoutinePanel/RoutinePanel';
import { Routine } from '../../types/Fisiofit/routine';
import { ALL_FEATURES } from '../../config/features';
import { Alert } from '../Alert/Alert';
import { ReservationPanelProps } from './types';
import { getPanelTitle } from './utils/messages';
import { getEmployeeBranchIds, getInitialForm } from './utils/form';
import { mutateUserProfileData } from './utils/cache';
import { getHourPickerEventTitle } from './utils/slots';
import { CloneReservationDialog } from './dialogs/CloneReservationDialog';
import { BlockSlotDialog } from './dialogs/BlockSlotDialog';
import { UnblockSlotDialog } from './dialogs/UnblockSlotDialog';
import { CancelUserDialog } from './dialogs/CancelUserDialog';
import { CancelReservationDialog } from './dialogs/CancelReservationDialog';
import { CancellationBanner } from './banners/CancellationBanner';
import { BlockedBanner } from './banners/BlockedBanner';
import { UnregisteredClientFields } from './fields/UnregisteredClientFields';
import { ClientField } from './fields/ClientField';
import { GroupClientsField } from './fields/GroupClientsField';
import { CouponField } from './fields/CouponField';
import { PriceField } from './fields/PriceField';
import { CommentField } from './fields/CommentField';
import { EmployeeField } from './fields/EmployeeField';
import { BranchField } from './fields/BranchField';
import { InformativeEmployeeField } from './fields/InformativeEmployeeField';
import { DayField } from './fields/DayField';
import { HourPickerField } from './fields/HourPickerField';
import { useReservationCoupons } from './hooks/useReservationCoupons';
import { useReservationCancellation } from './hooks/useReservationCancellation';
import { useHourSelection } from './hooks/useHourSelection';
import { useReservationRequests } from './hooks/useReservationRequests';
import { UserAttendance } from './fields/UserAttendance';
import { getPanelActions } from './utils/panelActions';
import { useReservationState } from './hooks/useReservationState';
import { ClientTypeField } from './fields/ClientTypeField';
import { ReservationTextField } from './fields/ReservationTextField';
import { CreatedByField } from './fields/CreatedByField';
import { getCouponNoteText, getCouponRefundText as getCouponRefundTextFor } from './utils/couponNotes';

export type { ReservationEvent } from './types';

export const ReservationPanel = ({
  resource,
  visible,
  onHide,
  onIsEditingChange,
  mutateReservations,
  clearInitialTemporaryEvent,
  isEditing = false,
  isDeleting = false,
  createModeReservationTypeId,
  initialHours,
  initialTempEventOverride,
  externalUserProfileId,
  preselectedExternalUser,
  preselectedBranchId,
  calendarRestrictions,
  onSaveSuccess,
}: ReservationPanelProps) => {
  const { locale } = useLocaleContext();
  const { organization } = useOrganizationContext();
  const { user: authUser } = useAuthContext();
  const { get } = useClient();
  const {
    isCreating,
    isModifying,
    clientType,
    setSelectedClientType,
    reservationType,
    reservationTypeFromId,
    findReservationTypeByBranchAndName,
    isGroupReservation,
    isUnregisteredClient,
    userHasWriteFeatures,
    hasRoutinesPermission,
    initialTemporaryEvent,
    isPast,
    hasStarted,
    isCanceled,
  } = useReservationState({ resource, isEditing, createModeReservationTypeId, initialTempEventOverride });
  const [showQueuedUsers, setShowQueuedUsers] = useState(false);
  const [isAttendanceMode, setIsAttendanceMode] = useState(false);

  const [showBlockDialog, setShowBlockDialog] = useState(false);
  const [showUnblockDialog, setShowUnblockDialog] = useState(false);

  const [showCloneDialog, setShowCloneDialog] = useState(false);

  // Estado para el panel de rutinas
  const [selectedUserForRoutine, setSelectedUserForRoutine] = useState<ExternalUserShort | null>(null);
  const [isEditingRoutine, setIsEditingRoutine] = useState(false);

  const showRequiredFields = isModifying && !isAttendanceMode;

  const isTouchDevice = Capacitor.isNativePlatform() || window.matchMedia('(hover: none)').matches;
  const initialReservationForm = useMemo(() => {
    return getInitialForm(
      resource,
      authUser,
      isCreating,
      reservationType,
      initialHours,
      preselectedExternalUser,
      preselectedBranchId,
      visible,
    );
  }, [resource, authUser, isCreating, reservationType, initialHours, preselectedExternalUser, preselectedBranchId, visible]);
  const validators = useMemo(
    () => getReservationFormValidators(reservationType, initialTemporaryEvent, isUnregisteredClient),
    [reservationType, initialTemporaryEvent, isUnregisteredClient],
  );
  const { form, setFormFields, isSaving, setIsSaving, hasChanged, validationErrors, resetForm, validate } =
    useForm<ReservationForm>(initialReservationForm, !visible, validators);

  const selectedEmployee = form.user?.id === authUser?.id ? authUser : form.user;
  const selectedEmployeeBranchIds = getEmployeeBranchIds(selectedEmployee);
  const { data: selectedEmployeeDetails, isLoading: employeeBranchesLoading } = useSWR(
    visible && isModifying && reservationType?.hasAssignedUsers && selectedEmployee?.id && selectedEmployeeBranchIds === undefined
      ? `/users/${selectedEmployee.id}`
      : null,
    get<User>,
  );
  const employeeBranchIds = selectedEmployeeBranchIds ?? getEmployeeBranchIds(selectedEmployeeDetails);

  const hourSelection = useHourSelection({
    visible,
    isModifying,
    resource,
    reservationType,
    reservationTypeFromId,
    form,
    setFormFields,
    initialTemporaryEvent,
    clearInitialTemporaryEvent,
  });

  const hasSelectedExternalUsers = (form?.externalUsers && form.externalUsers.length > 0) || isUnregisteredClient;

  const coupons = useReservationCoupons({ organization, reservationType, resource, form, isModifying });

  const handleHide = () => {
    hourSelection.setTemporaryEvent(null);
    setIsAttendanceMode(false);
    setSelectedClientType(undefined);
    setSelectedUserForRoutine(null);
    coupons.reservationUserCoupon.current = undefined;
    onHide();
  };

  const cancellation = useReservationCancellation({
    resource,
    visible,
    reservationType,
    isGroupReservation,
    isUnregisteredClient,
    form,
    setFormFields,
    organization,
    externalUserProfileId,
    mutateReservations,
    mutateOrgCoupons: coupons.mutateOrgCoupons,
    mutateActiveUserCoupons: coupons.mutateActiveUserCoupons,
    mutateSelectedUserCoupon: coupons.mutateSelectedUserCoupon,
    onHide: handleHide,
  });

  // Consulta SWR para obtener la rutina del usuario seleccionado
  const { data: userRoutine, isLoading: isLoadingRoutine } = useSWR(
    selectedUserForRoutine ? `/external-users/${selectedUserForRoutine.id}/latest-routine` : null,
    get<Routine>,
  );

  const { handleSave, handleBlock, handleUnblock, handleAttendanceSave } = useReservationRequests({
    resource,
    isCreating,
    isEditing,
    reservationType,
    findReservationTypeByBranchAndName,
    isGroupReservation,
    isUnregisteredClient,
    organization,
    authUser,
    form,
    validate,
    setIsSaving,
    resetForm,
    initialTemporaryEvent,
    invalidHourSelection: hourSelection.invalidHourSelection,
    canceledUsers: cancellation.canceledUsers,
    setCanceledUsers: cancellation.setCanceledUsers,
    externalUserProfileId,
    mutateReservations,
    mutateExistingReservations: hourSelection.mutateExistingReservations,
    mutateOrgCoupons: coupons.mutateOrgCoupons,
    mutateActiveUserCoupons: coupons.mutateActiveUserCoupons,
    mutateSelectedUserCoupon: coupons.mutateSelectedUserCoupon,
    onSaveSuccess,
    onHide: handleHide,
  });

  const getGroupExternalUserButtonProps = (externalUser: ExternalUserShort, isConfirmed: boolean): ButtonProps | undefined => {
    if (!isModifying) return undefined;
    if (hasStarted) {
      return undefined;
    }
    return {
      icon: PrimeIcons.TRASH,
      className: 'p-button-rounded p-button-danger p-button-text',
      onClick: (e) => {
        e.preventDefault();
        e.stopPropagation();
        if (isConfirmed) {
          cancellation.handleUserCancellation(externalUser);
        } else {
          setFormFields({
            usersBeforeSave: form?.usersBeforeSave?.filter((user) => user.id !== externalUser.id) ?? [],
          });
        }
      },
    };
  };

  const renderUserAttendance = (user: ExternalUserShort) => (
    <UserAttendance
      user={user}
      resource={resource}
      hasStarted={hasStarted}
      isEditingAttendance={isEditing && isAttendanceMode}
      form={form}
      setFormFields={setFormFields}
    />
  );

  const getCouponRefundText = () =>
    getCouponRefundTextFor(coupons.reservationUserCoupon.current?.usesCount, coupons.couponWasPurchased);

  const renderCouponNote = () => {
    const text = getCouponNoteText({
      form,
      resource,
      isModifying,
      isEditing,
      selectedUserCoupon: coupons.selectedUserCoupon,
      selectedCouponHasUses: coupons.selectedCouponHasUses,
      usesCount: coupons.reservationUserCoupon.current?.usesCount,
      couponWasPurchased: coupons.couponWasPurchased,
    });
    if (!text) return null;
    return (
      <Alert textSize='small' severity='info'>
        {text}
      </Alert>
    );
  };

  const handleAttendanceClick = async (attendanceMode = true): Promise<boolean> => {
    if (attendanceMode) {
      setIsAttendanceMode(true);
      onIsEditingChange?.(true);
      return true;
    }
    return cancellation.handleDelete();
  };

  const allowedFeatures: AllowedFeatures = {
    edit: [reservationType?.features.manage as string],
    create: [reservationType?.features.manage as string],
    delete: [reservationType?.features.manage as string],
  };

  // Función para cerrar el panel de rutinas
  const handleCloseRoutinePanel = () => {
    setIsEditingRoutine(false);
    setSelectedUserForRoutine(null);
  };

  const usersToShow = useMemo(() => (form?.confirmedExternalUsers ?? []).concat(form?.usersBeforeSave ?? []), [form]);
  const userArrayLength = Math.max(reservationType?.groupSize ?? 0, usersToShow.length);

  const canModifyDate = (isCreating || (isEditing && !!reservationType?.canModifyDate)) && !isPast;
  const panelActions = getPanelActions({
    isCreating,
    isEditing,
    isCanceled,
    isBlocked: !!resource?.blocked,
    isPast,
    hasStarted,
    isUnregisteredClient,
    isAttendanceMode,
    reservationType,
  });
  const saveActions = {
    attendance: handleAttendanceSave,
    save: handleSave,
    none: () => Promise.resolve(false),
  };

  return (
    <>
      <CloneReservationDialog
        visible={showCloneDialog}
        onHide={() => setShowCloneDialog(false)}
        resource={resource}
        isCloneable={reservationType?.isCloneable}
        usersToShow={usersToShow}
        onCloned={() => {
          mutateReservations();
          coupons.mutateSelectedUserCoupon(undefined);
          if (externalUserProfileId && organization) {
            mutateUserProfileData(organization, externalUserProfileId);
          }
          handleHide();
        }}
      />

      <BlockSlotDialog
        visible={showBlockDialog}
        onHide={() => setShowBlockDialog(false)}
        onConfirm={() => {
          handleBlock().then((success) => {
            if (success) {
              setShowBlockDialog(false);
              handleHide();
            }
          });
        }}
        isSaving={isSaving}
      />

      <CancelUserDialog
        visible={cancellation.showCancellationDialog}
        onHide={() => {
          cancellation.setShowCancellationDialog(false);
          cancellation.setUserToCancel(undefined);
        }}
        userToCancel={cancellation.userToCancel}
        cancellationForm={cancellation.cancellationForm}
        setCancellationFormFields={cancellation.setCancellationFormFields}
        cancellationErrors={cancellation.cancellationErrors}
        onConfirm={cancellation.handleConfirmCancellation}
        isCanceling={cancellation.isCanceling}
      />

      <CancelReservationDialog
        visible={cancellation.showGroupCancellationDialog}
        onHide={() => cancellation.setShowGroupCancellationDialog(false)}
        resource={resource}
        isGroupReservation={isGroupReservation}
        isCancelingGroupWithMultipleUsers={cancellation.isCancelingGroupWithMultipleUsers}
        getCouponRefundText={getCouponRefundText}
        cancellationForm={cancellation.cancellationForm}
        setCancellationFormFields={cancellation.setCancellationFormFields}
        cancellationErrors={cancellation.cancellationErrors}
        onConfirm={cancellation.handleConfirmCancellation}
        isCanceling={cancellation.isCanceling}
      />

      <UnblockSlotDialog
        visible={showUnblockDialog}
        onHide={() => setShowUnblockDialog(false)}
        onConfirm={() => {
          handleUnblock().then((success) => {
            if (success) {
              setShowUnblockDialog(false);
            }
          });
        }}
        isSaving={isSaving}
      />

      <Panel
        panelType='resourcePanel'
        title={getPanelTitle(isCreating, isEditing, reservationType?.name, resource)}
        resourceName={isAttendanceMode ? 'asistencias' : 'reserva'}
        visible={visible}
        onHide={handleHide}
        allowedFeatures={allowedFeatures}
        onDelete={
          panelActions.deleteAction &&
          (panelActions.deleteAction === 'attendance' ? handleAttendanceClick : () => cancellation.handleDelete())
        }
        isDeleting={isDeleting}
        onSave={saveActions[panelActions.saveAction]}
        onIsEditingChange={(editing) => {
          if (!editing) {
            setIsAttendanceMode(false);
          }
          onIsEditingChange?.(editing);
        }}
        hasChanged={hasChanged || !!initialHours}
        isSaving={isSaving}
        isEditing={isEditing}
        isCreating={isCreating}
        resetForm={resetForm}
        className='reservation-panel'
        hasCustomDelete={!isUnregisteredClient}
        customDeleteLabel='Cancelar reserva'
        customDeleteConfirmationDialogProps={{
          message: `¿Estás seguro de que quieres cancelar la reserva?`,
          header: 'Cancelar reserva',
          acceptLabel: 'Sí, cancelar',
          rejectLabel: 'Volver',
        }}
        hideEditButton={panelActions.hideEditButton}
        hideDeleteButton={panelActions.hideDeleteButton}
        showAttendanceButton={panelActions.showAttendanceButton}
        onAttendanceClick={handleAttendanceClick}
        attendanceButtonLabel={reservationType?.groupSize === 1 ? 'Asistencia' : 'Asistencias'}
        HeaderComponent={
          panelActions.showCloneButton ? (
            <div className='panel-header-with-button'>
              <h4>{getPanelTitle(isCreating, isEditing, reservationType?.name, resource)}</h4>
              <Button
                label='Clonar'
                icon={PrimeIcons.COPY}
                className='p-button-primary'
                onClick={() => setShowCloneDialog(true)}
                outlined
              />
            </div>
          ) : undefined
        }
        blockButton={
          panelActions.showBlockButton
            ? {
                label: 'Bloquear',
                icon: PrimeIcons.LOCK,
                onClick: () => setShowBlockDialog(true),
              }
            : undefined
        }
        unblockButton={
          panelActions.showUnblockButton
            ? {
                label: 'Desbloquear',
                icon: PrimeIcons.UNLOCK,
                onClick: () => setShowUnblockDialog(true),
              }
            : undefined
        }
      >
        <form
          className={classNames({
            'form-loading': isSaving,
          })}
          onSubmit={handleSave}
        >
          {isCanceled && <CancellationBanner resource={resource} />}

          {resource?.blocked && <BlockedBanner />}

          {isCreating && reservationType?.groupSize === 1 && reservationType?.hasUnregisteredClients && (
            <ClientTypeField clientType={clientType} onChange={setSelectedClientType} />
          )}

          {!isCanceled &&
            !resource?.blocked &&
            reservationType?.groupSize === 1 &&
            (isUnregisteredClient ? (
              <UnregisteredClientFields
                form={form}
                setFormFields={setFormFields}
                validationErrors={validationErrors}
                isModifying={isModifying}
                showRequiredFields={showRequiredFields}
                showCreateUserButton={!isModifying && !!resource && userHasWriteFeatures}
                resource={resource}
              />
            ) : (
              <ClientField
                form={form}
                setFormFields={setFormFields}
                validationErrors={validationErrors}
                isCreating={isCreating}
                isModifying={isModifying}
                isPast={isPast}
                showRequiredFields={showRequiredFields}
                preselectedExternalUser={preselectedExternalUser}
                reservationType={reservationType}
                resource={resource}
                renderUserAttendance={renderUserAttendance}
              />
            ))}

          {!isCanceled && !resource?.blocked && reservationType?.hasCustomName && (
            <ReservationTextField
              labelTitle='Nombre'
              elementId='reservation-custom-name'
              placeholder='Ej: Sesión especial de navidad'
              value={form?.customName}
              onChange={(customName) => setFormFields({ customName })}
              isModifying={isModifying}
              invalid={!!validationErrors?.customName}
            />
          )}

          {!isCanceled && !resource?.blocked && reservationType?.hasTitle && (
            <ReservationTextField
              labelTitle='Título'
              elementId='reservation-title'
              placeholder='Ej: Rehabilitación de rodilla'
              value={form?.title}
              onChange={(title) => setFormFields({ title })}
              isModifying={isModifying}
            />
          )}

          {!isCanceled && !resource?.blocked && isGroupReservation && reservationType && (
            <GroupClientsField
              form={form}
              setFormFields={setFormFields}
              validationErrors={validationErrors}
              reservationType={reservationType}
              usersToShow={usersToShow}
              userArrayLength={userArrayLength}
              canceledUsers={cancellation.canceledUsers}
              isEditing={isEditing}
              isModifying={isModifying}
              isPast={isPast}
              showRequiredFields={showRequiredFields}
              showQueuedUsers={showQueuedUsers}
              setShowQueuedUsers={setShowQueuedUsers}
              hasRoutinesPermission={hasRoutinesPermission}
              selectedUserForRoutine={selectedUserForRoutine}
              setSelectedUserForRoutine={setSelectedUserForRoutine}
              isLoadingRoutine={isLoadingRoutine}
              userRoutine={userRoutine}
              getGroupExternalUserButtonProps={getGroupExternalUserButtonProps}
              renderUserAttendance={renderUserAttendance}
            />
          )}

          {!isCanceled &&
            !resource?.blocked &&
            (isModifying || resource?.userCouponId) &&
            reservationType?.hasPrice &&
            reservationType?.canUseCoupon &&
            !isUnregisteredClient &&
            (!isAttendanceMode || !isPast) && (
              <CouponField
                form={form}
                setFormFields={setFormFields}
                validationErrors={validationErrors}
                resource={resource}
                isModifying={isModifying}
                hasSelectedExternalUsers={hasSelectedExternalUsers}
                activeUserCoupons={coupons.activeUserCoupons}
                activeUserCouponsLoading={coupons.activeUserCouponsLoading}
                orgCoupons={coupons.orgCoupons}
                orgCouponsLoading={coupons.orgCouponsLoading}
                usersWithActiveCoupons={coupons.usersWithActiveCoupons}
                usersWithActiveCouponsLoading={coupons.usersWithActiveCouponsLoading}
                couponSelectionDataLoading={coupons.couponSelectionDataLoading}
                selectedUserCoupon={coupons.selectedUserCoupon}
                selectedUserCouponLoading={coupons.selectedUserCouponLoading}
                reservationUserCoupon={coupons.reservationUserCoupon.current}
                renderCouponNote={renderCouponNote}
              />
            )}

          {!isCanceled && !resource?.blocked && reservationType?.hasPrice && (
            <PriceField
              form={form}
              setFormFields={setFormFields}
              validationErrors={validationErrors}
              isEditable={isModifying && (!isAttendanceMode || !isPast)}
              required={showRequiredFields && reservationType.priceRequired}
              hasSelectedExternalUsers={hasSelectedExternalUsers}
              resource={resource}
            />
          )}

          {reservationType?.hasComment && (
            <CommentField
              form={form}
              setFormFields={setFormFields}
              validationErrors={validationErrors}
              isEditable={isModifying && (!isAttendanceMode || !isPast)}
            />
          )}

          {!isCanceled && reservationType?.hasAssignedUsers && (
            <EmployeeField
              form={form}
              validationErrors={validationErrors}
              isModifying={isModifying}
              showRequiredFields={showRequiredFields}
              reservationType={reservationType}
              onChange={hourSelection.handleEmployeeChange}
            />
          )}

          <BranchField
            form={form}
            setFormFields={setFormFields}
            validationErrors={validationErrors}
            isModifying={isModifying}
            showRequiredFields={showRequiredFields}
            reservationType={reservationType}
            employeeBranchIds={employeeBranchIds}
            employeeBranchesLoading={employeeBranchesLoading}
          />

          {!isCanceled && reservationType?.hasInformativeUser && (
            <InformativeEmployeeField form={form} setFormFields={setFormFields} isModifying={isModifying} />
          )}

          <DayField
            form={form}
            validationErrors={validationErrors}
            canModifyDate={canModifyDate}
            showRequiredFields={showRequiredFields}
            isTouchDevice={isTouchDevice}
            locale={locale}
            onChange={hourSelection.handleDayChange}
          />

          <HourPickerField
            form={form}
            validationErrors={validationErrors}
            canModifyDate={canModifyDate}
            canSelectHour={hourSelection.canSelectHour}
            showRequiredFields={showRequiredFields}
            isTouchDevice={isTouchDevice}
            isLoading={hourSelection.existingReservationsLoading}
            reservationType={reservationType}
            calendarRestrictions={calendarRestrictions}
            scrollTime={hourSelection.scrollTime}
            blockedEvents={hourSelection.blockedEvents}
            temporaryEvent={hourSelection.temporaryEvent}
            initialTemporaryEvent={initialTemporaryEvent}
            invalidHourSelection={hourSelection.invalidHourSelection}
            newEventTitle={getHourPickerEventTitle(resource, reservationType, !isEditing)}
            onTemporaryEventChange={hourSelection.handleTemporaryEventChange}
            onHoursChange={hourSelection.handleHoursChange}
          />

          {!isCreating && resource?.createdBy && <CreatedByField createdBy={resource.createdBy} />}
        </form>
      </Panel>

      {hasRoutinesPermission && (
        <RoutinePanel
          resource={userRoutine}
          visible={!!userRoutine}
          onHide={handleCloseRoutinePanel}
          isEditing={isEditingRoutine}
          isDeleting={false}
          onIsEditingChange={setIsEditingRoutine}
          allowedFeatures={{
            edit: [ALL_FEATURES.ROUTINES_WRITE],
            delete: [ALL_FEATURES.ROUTINES_WRITE],
          }}
        />
      )}
    </>
  );
};
